---
title: "Reddit MCP Server: Live Reddit Data for Your Agent"
description: "Reddit's API needs an approved OAuth app and caps you at 100 calls a minute. Connect a Reddit MCP server to your agent in one config block, priced in USD."
date: "2026-07-06"
dateModified: "2026-09-06"
author: "Kevin Wang"
tags: ["mcp", "reddit", "ai-agents", "data-api"]
keyword: "reddit mcp server"
ogImage: "/og/reddit-mcp-server.png"
---

export const redditTimeline = [
  { date: "Apr 18, 2023", title: "Reddit announces it will start charging for API access" },
  { date: "May 31, 2023", title: "Apollo is quoted around $20M a year to keep running" },
  { date: "Jun 1, 2023", title: "Commercial price set at $0.24 per 1,000 API calls", highlight: true },
  { date: "Jun 12, 2023", title: "More than 7,000 subreddits go dark in protest" },
  { date: "Jun 30, 2023", title: "Apollo and other third-party apps shut down", highlight: true },
  { date: "Late 2025", title: "Even a free, non-commercial app needs Reddit's approval to call the API" },
];

<Callout type="tip" title="Key findings">

Your agent speaks MCP, so you add Reddit data by pointing it at a Reddit MCP server, not by registering an OAuth app and writing a scraper. AnyAPI is one HTTP MCP endpoint that exposes Reddit search, subreddit listings, post comments, and subreddit metadata behind one key, with no Reddit account, priced in USD per 1k. It's one block in `claude_desktop_config.json` plus a restart.

</Callout>

Ask your agent "what are people saying about our launch on Reddit this week." With plain web tools it can't really answer. A web search gets it links and snippets, but the moment it tries to open a thread to read the actual posts and comments, Reddit gates the logged-out request behind a login wall and blocks it, so the agent stalls or just tells you it can't. You don't get Reddit as data.

There are three ways to get that data, and in 2026 each one taxes a normal builder: Reddit's official API now wants an approved OAuth app and caps you at 100 calls a minute, DIY scraping is a maintenance treadmill that trips a rate limit and stops, and the free MCP servers on GitHub still make you register your own Reddit app and run a local process you patch when it breaks.

So here's the fix. **An MCP server turns Reddit into a tool your agent calls, with no Reddit app to register and nothing to keep alive.**

## What your agent can and can't do out of the box

Most agents ship with generic tools, and none of them return a typed Reddit record. The default set is a web search and a browser. The search turns up Reddit links, but the browser usually can't open the thread behind them: Reddit blocks automated, logged-out requests, so the fetch comes back as a login wall instead of the page. Even when something does load, it's HTML to parse, not a post, a comment thread, or a search result as structured fields.

That gap matters for the job you're actually doing. A web search returns a ranked list of links. Your agent then has to get past Reddit's block or login wall to open each one, guess which thread is relevant, and parse whatever HTML it managed to load. It's brittle when it works at all, and it burns tokens on the parts a structured record already solved.

The difference is between reading a page and getting data. A structured record hands you the title, the author, the score, the comment count, the subreddit, and the timestamp as fields you can sort and filter on. A page is a blob you hope contains them. Most agents ship the blob-reading tools; they don't ship the record.

The supported way to add the record is MCP. An MCP-native client like Claude Desktop, Cursor, or Claude Code connects to external tool servers so your agent can reach an API without you writing a native plugin. You don't fork the client. You point it at a server.

So the question isn't "can my agent scrape Reddit." It's which Reddit MCP server you plug in.

## What happened to the Reddit API

Getting Reddit data comes down to three paths, and by 2026 each one costs a normal builder something real. Here's each one, and where it slows you down.

The official Reddit API is gated and rate-limited. There's a free tier, but since late 2025 even a free, non-commercial app has to be approved by Reddit before it can make a call, and it caps you at 100 queries a minute with OAuth (10 without). Commercial use is paid at $0.24 per 1,000 calls. Before you fetch a single post you're registering an app, wiring OAuth, and waiting on approval.

DIY scraping is a treadmill. You point a script at Reddit, and it works for a handful of calls. Then you trip a rate limit or an auth wall, the script stops, and you're rotating tokens and re-patching the parser every time the markup shifts. That's ongoing work you own forever, not a one-time integration.

**The 2023 API price change is why third-party Reddit access got hard in the first place.** Reddit announced paid API access in April 2023, set the price at $0.24 per 1,000 calls, and Apollo, the most popular third-party client, was quoted about $20M a year to keep running and [shut down that June](https://techcrunch.com/2023/06/08/popular-third-party-reddit-app-apollo-is-shutting-down-as-a-result-of-reddits-new-api-pricing/). More than 7,000 subreddits went dark in protest, per the [Reddit API controversy record](https://en.wikipedia.org/wiki/Reddit_API_controversy). The tools people relied on to read Reddit programmatically went away, and the replacement path is either Reddit's gated API or a scraper you maintain.

<Timeline
  items={redditTimeline}
  caption="Reddit API access, 2023-2026. The June 2023 pricing change killed the third-party clients, and the late-2025 approval requirement is why even a free app is no longer a five-minute setup."
/>

## How to connect a Reddit MCP server to Claude Desktop in 3 steps

One HTTP endpoint, one config block, one restart. Here's the exact thing I run to give an agent live Reddit data.

**Step 1. Get a key.** Sign up at [getanyapi.com](/catalog) and grab an API key. You pay per request in USD with no subscription, so a Reddit search costs what it costs and nothing sits on a monthly bill. If the agent needs to provision its own key with no human in the loop, [agent self-signup](/docs/agent-self-signup) does that too.

**Step 2. Add the MCP server to Claude Desktop.** Open `claude_desktop_config.json` (Settings, Developer, Edit Config, or edit `~/Library/Application Support/Claude/claude_desktop_config.json` directly) and add this block, then restart Claude Desktop:

```json
{
  "mcpServers": {
    "anyapi": {
      "command": "npx",
      "args": [
        "mcp-remote",
        "https://api.getanyapi.com/mcp",
        "--header",
        "Authorization:Bearer ${ANYAPI_KEY}"
      ]
    }
  }
}
```

That's it: **it's one config block and a restart, not a Reddit app you register and a scraper you maintain.** There's no local server code to write; `mcp-remote` just bridges Claude Desktop to the hosted HTTP endpoint. One quirk to copy exactly: the header arg is `Authorization:Bearer` with no space, since `mcp-remote` splits the argument on the space otherwise. It still sends a normal `Authorization: Bearer` header on the wire. The same endpoint works from any MCP client:

<CodeTabs labels={["Claude Desktop", "Claude Code", "Cursor"]}>

```json
{
  "mcpServers": {
    "anyapi": {
      "command": "npx",
      "args": [
        "mcp-remote",
        "https://api.getanyapi.com/mcp",
        "--header",
        "Authorization:Bearer ${ANYAPI_KEY}"
      ]
    }
  }
}
```

```bash
claude mcp add anyapi --transport http https://api.getanyapi.com/mcp --header "Authorization: Bearer $ANYAPI_KEY"
```

```json
{
  "mcpServers": {
    "anyapi": {
      "url": "https://api.getanyapi.com/mcp",
      "headers": { "Authorization": "Bearer ${ANYAPI_KEY}" }
    }
  }
}
```

</CodeTabs>

**Step 3. Ask in natural language.** Now you just talk to the agent. Ask it to search Reddit for a topic and it calls `search_apis` to find the right endpoint, then `run_api` with `reddit.search` to fetch it. Here's the trimmed record that comes back for a query:

```json
{
  "posts": [
    {
      "title": "Safari's new MCP server lets coding agents inspect and debug websites",
      "subreddit": "apple",
      "score": 430,
      "numComments": 17,
      "permalink": "https://reddit.com/r/apple/comments/1n8x2k/...",
      "createdUtc": 1751673600
    },
    {
      "title": "Going local is life changing",
      "subreddit": "LocalLLM",
      "score": 414,
      "numComments": 92,
      "permalink": "https://reddit.com/r/LocalLLM/comments/1n7p9d/...",
      "createdUtc": 1751587200
    }
  ],
  "nextCursor": "t3_1n8x2k"
}
```

Your agent gets fields it can rank and filter on instead of a page it has to read. No Reddit login, no OAuth app to register, and no Reddit rate limit to manage in your own code.

<Figure
  src="/og/screenshots/reddit-mcp-server-claude-desktop.png"
  alt="Claude Desktop asked to search Reddit for MCP server discussion: on its own the response is interrupted, then with the anyapi MCP server it returns the week's top posts."
  caption="The same ask in Claude Desktop. On its own the agent stalls on its real-time access limits and the response gets interrupted; once it uses the anyapi MCP server, it pulls the week's top Reddit posts on MCP servers back as data."
  width={2396}
  height={1546}
/>

## What Reddit data can you pull?

The catalog exposes Reddit as typed endpoints your agent calls by name. Here's the set and what each one returns:

| Endpoint | What it returns | USD / 1k |
| --- | --- | --- |
| **`reddit.trending_posts`** | What's trending across all of Reddit right now | <Price sku="reddit.trending_posts" /> |
| **`reddit.search`** | Post search across all of Reddit by query, with field operators | <Price sku="reddit.search" /> |
| **`reddit.subreddit_search`** | Post search inside one subreddit, by query and timeframe | <Price sku="reddit.subreddit_search" /> |
| **`reddit.subreddit_posts`** | A subreddit listing (hot, new, or top) | <Price sku="reddit.subreddit_posts" /> |
| **`reddit.post`** | A single post by URL: full body text, score, upvote ratio | <Price sku="reddit.post" /> |
| **`reddit.user_posts`** | A user's posts by username, sorted new, top, hot, or controversial | <Price sku="reddit.user_posts" /> |
| **`reddit.subreddit_details`** | Subreddit metadata: active users, description, category | <Price sku="reddit.subreddit_details" /> |
| **`reddit.profile`** | A user's public profile: karma split, post and comment counts, bio, account age | <Price sku="reddit.profile" /> |
| **`reddit.user_comments`** | A user's comments by username, with the parent post title on every item | <Price sku="reddit.user_comments" /> |
| **`reddit.post_comments`** | Comments on a post: author, body, timestamp, and the comment each reply answers | <Price sku="reddit.post_comments" /> |
| **`reddit.post_transcript`** | The spoken transcript from a Reddit video post | <Price sku="reddit.post_transcript" /> |

One key, one bill, all in USD. The schema is normalized across sources, so the record shape your agent parses stays the same. A `reddit.search` call returns the same fields today and next month, which means the code that reads `post.score` doesn't break under you.

Live pricing sits on [/catalog](/catalog), so check there for the current numbers and the full input schema per endpoint.

<Figure
  src="/og/screenshots/reddit-mcp-server-catalog.png"
  alt="The Reddit card in the AnyAPI catalog: 12 endpoints across 7 providers, priced from $0.20 per 1k requests."
  caption="The Reddit card in the catalog: 12 endpoints under one key, priced in USD per 1k requests."
  width={834}
  height={501}
/>

## Beyond Reddit: one MCP server, 200+ APIs

The Reddit endpoints aren't a one-off. The same config block and the same two tools, `search_apis` and `run_api`, give your agent the rest of the catalog: TikTok, YouTube, LinkedIn, plus company and people enrichment. You don't add a new server per platform or juggle a second key. The agent asks what's available, picks the endpoint, and runs it, and every result comes back as a normalized JSON record, priced in USD.

That's the point of pointing an agent at one MCP endpoint instead of a pile of scrapers: it grows without you rewiring anything. Browse the full set on [/catalog](/catalog), or read the [MCP server reference](/docs/mcp-server) for the tool details.

## Reddit MCP server options, compared

Search "reddit mcp server" and you'll find a few kinds of results. None of them is a hosted data source you can point at any subreddit at scale without managing Reddit's rate limits yourself. Here's what each one actually is, stated plainly.

The first kind is community OSS servers, like [karanb192/reddit-mcp-buddy](https://github.com/karanb192/reddit-mcp-buddy) (the most popular, and genuinely well built) or [jordanburke/reddit-mcp-server](https://github.com/jordanburke/reddit-mcp-server). They're single-purpose and free, and they run locally as a process on your machine. A few, buddy included, have an anonymous mode with no Reddit account, but it's capped at Reddit's 10-calls-a-minute floor; to get real headroom you register your own Reddit app and paste in a client ID and secret, and the write-capable ones want your Reddit username and password. Either way you're running and updating the server, and you only get Reddit.

The second kind is account-action connectors. These OAuth into your own Reddit account so your agent can act as you: post, comment, read your own feed. That's a different job from pulling structured posts and comments across many subreddits you don't own. It needs your account, and it's built for actions, not bulk reads.

The third kind is Reddit's own API. It's gated and rate-limited, and since late 2025 it needs approval even for a free app, so you can't just drop in a key and start pulling data for an agent.

<Chart caption="Reddit MCP server options compared on the things that decide the pick. AnyAPI is a maintained, no-account data source priced in USD; the OSS servers and account connectors each fit a narrower job.">

| Option | Maintained for you? | No Reddit app to register? | No rate-limit babysitting? | USD per request? | Many platforms, one key? | Any MCP client? |
| --- | --- | --- | --- | --- | --- | --- |
| **Community OSS Reddit MCP** | No, you patch it | App past 10/min | No, Reddit's limits | Free | Reddit only | Any client |
| **Account-action connector** | Yes | No, needs your account | Account-scoped | Subscription | Your account only | Any client |
| **Official Reddit API** | Yes | No, approval required | No, 100/min | $0.24/1k commercial | Reddit only | n/a |
| **AnyAPI** | Yes | Yes | Yes | Yes | Yes | Yes |

</Chart>

## When not to use a hosted Reddit MCP server

I'll name the downside instead of pretending there isn't one. Reddit's own commercial API, at $0.24 per 1,000 calls, is cheaper per call than most of the Reddit endpoints here, and if all you want is free, local, read-only Reddit access, an OSS server like buddy does that well. Both cost less money than a paid gateway.

That $0.24 is a floor as much as a rate, though. Reddit publishes no commercial rate card, and buyers consistently report a minimum around $12,000 a month for 50 million calls, so it only undercuts a per-call price if you use the volume you are paying for. eSEOspace works the effective cost per call through that minimum in its <a href="https://eseospace.com/blog/reddit-api-pricing/" target="_blank" rel="noopener noreferrer">Reddit API pricing breakdown</a>.

It changes once you weigh setup and scale. If you want structured Reddit data your agent can call today with no account, no approval wait, no rate-limit code, and the same call shape across 200+ other APIs, that's the case AnyAPI is for. **The free options stay free until you count the Reddit app you register at volume and the process you keep patched; a hosted endpoint is the one here you don't run or fix yourself.**

## Reddit's official API, and where it runs out

**Does Reddit have an API?**

Yes. It has a free tier for non-commercial use and a paid commercial tier at $0.24 per 1,000 calls. Since late 2025, even a free app needs Reddit's approval before it can make a call.

**Is the Reddit API free?**

For non-commercial use under the rate limits, yes: 100 queries a minute with OAuth, 10 without. Commercial use or higher volume is paid. See [is the Reddit API free](/blog/is-the-reddit-api-free) for the full breakdown.

**Why not just use the official Reddit API?**

You can, and for most endpoints it's cheaper per call. You take on the OAuth app, the approval wait, and the 100-per-minute limit in exchange, and you only get Reddit. The MCP endpoint trades a bit of per-call cost for no setup and access to 200+ other APIs under the same key.

## Running this in production

**What Reddit data can I get?**

Trending posts, post search across all of Reddit, in-subreddit search, subreddit listings, single posts with their full body, a user's posts and comments, subreddit metadata, user profiles, top-level comments on a post, and transcripts from Reddit video posts. Each is a typed endpoint your agent calls by name.

**Will this break like a scraper?**

AnyAPI maintains it, with schema normalization and automatic fallback. If a source shifts, the fix happens behind the endpoint, so your agent config and your parser don't change.

**Can my agent pull Reddit data autonomously?**

Yes. It runs unattended and calls the tools on its own, and a key with a spend cap bounds what it can cost. No human has to paste anything mid-run.

## Frequently asked questions

**What is a Reddit MCP server?**

An MCP server that exposes Reddit data as tools your agent calls. Instead of writing a scraper or wiring OAuth, you connect the server once and the agent runs Reddit search, subreddit listings, and comment fetches as named tools.

**Is there a free Reddit MCP server?**

Yes, community OSS ones exist on GitHub. They run locally, and most call Reddit's own API, so past the anonymous floor you register your own Reddit app, run the server yourself, and maintain it. That's what the zero price costs you.

**Can I use a Reddit MCP server with Claude Desktop?**

Yes. Add the server to `claude_desktop_config.json` and restart. The config block above is the exact one, and the same endpoint works in Claude Code and Cursor too.

**Do I need a Reddit account or OAuth app?**

No. You call the endpoint with an AnyAPI key, not Reddit credentials. There's no app to register, no OAuth to wire, and no approval to wait on.

**How much does Reddit data cost here?**

Per request, in USD. Trending posts run <Price sku="reddit.trending_posts" /> per 1,000 calls, search <Price sku="reddit.search" />, in-subreddit search <Price sku="reddit.subreddit_search" />, subreddit listings <Price sku="reddit.subreddit_posts" />, subreddit details <Price sku="reddit.subreddit_details" />, user posts <Price sku="reddit.user_posts" />, single posts <Price sku="reddit.post" />, user comments <Price sku="reddit.user_comments" />, post comments <Price sku="reddit.post_comments" />, profiles <Price sku="reddit.profile" />, and post transcripts <Price sku="reddit.post_transcript" />. See the [catalog](/catalog) for live pricing on every endpoint.

<CTA to="/catalog" note="Every Reddit endpoint priced in plain USD, billed per request, nothing to expire. New accounts start with free credit, no card required.">
Browse the catalog
</CTA>

<RelatedGuides items={[
  { to: "/blog/is-the-reddit-api-free", title: "Is the Reddit API free?", blurb: "What Reddit's free and commercial tiers actually cost, and the limits." },
  { to: "/blog/linkedin-mcp-server", title: "LinkedIn MCP server", blurb: "Live LinkedIn data for your agent through one MCP endpoint." },
  { to: "/blog/how-an-ai-agent-pays-for-an-api-call-x402", title: "How an AI agent pays for an API call", blurb: "The pay-per-request model that lets an agent buy data with no account." },
  { to: "/docs/mcp-server", title: "AnyAPI MCP server docs", blurb: "One endpoint, every catalog API, callable as an agent tool." },
]} />

AnyAPI handles compliance routing, and you're responsible for lawful use of the data you pull ([acceptable use](/acceptable-use)).
